import { getCasinoGameDetails, getMatchExposure, getMyBets } from "../../../apiservices/CasionApi";
import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router";
import BetTableSicbo from "../../casinoComponents/betTableSicbo";
import CasinoHeading from "../../casinoComponents/casinoHeading";
import CasinoLastResult from "../../casinoComponents/casinoLastResult";
import CasinoTableLayout from "../../casinoComponents/casinoTableLayout";
import MyBet from "../../casinoComponents/myBet";
import PlaceBet from "../../casinoComponents/placeBet/NoBet";
import ViewMoreModal from "../../../components/ViewMoreModal";
import ResultSicbo from "../resultui/resultSicbo/ResultSicbo";
import { extractCasinoGame } from "../../../utils/casinoGame";
import { displayNetOdds } from "../../../utils/casinoNetOdds";
import "../../../styles/games/sicbo.css";
import RefVideo from "./RefVideo";

// Sic Bo and Sic Bo 2 share the reference template (same markup; title / results keyed by route)
const TITLES = { sicbo: "Sic Bo", sicbo2: "Sic Bo 2" };


// Helper function to normalize string for matching
const normalizeString = (str) => {
  if (!str) return "";
  return str
    .replace(/\s*\(\s*/g, '(')
    .replace(/\s*\)\s*/g, ')')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
};

// Helper function to create all exposure key variations
const createExposureKeys = (selection) => {
  if (!selection) return [];
  const keys = [
    selection,
    selection.toLowerCase(),
    selection.toLowerCase().trim(),
    selection.trim(),
    normalizeString(selection),
  ];
  // Remove duplicates
  return [...new Set(keys.filter(k => k))];
};

export default function Sicbo() {
  const [gameData, setGameData] = useState(null);
  const [subData, setSubData] = useState([]);
  const [betValue, setBetValue] = useState("");
  const [betType, setBetType] = useState("");
  const [selectedSelection, setSelectedSelection] = useState("");
  const [selectedBetData, setSelectedBetData] = useState(null);
  const [showPlaceBet, setShowPlaceBet] = useState(false);
  const [iframeSrc, setIframeSrc] = useState("");
  const [exposures, setExposures] = useState({});
  const [myBets, setMyBets] = useState([]);
  const [showViewMoreModal, setShowViewMoreModal] = useState(false);

  const { pathname } = useLocation();

  const gameType = extractCasinoGame(pathname) === "sicbo2" ? "sicbo2" : "sicbo";
  // Reference game-header / result-modal title (lastresults2 res1.cname)
  const gameName = TITLES[gameType];

  useEffect(() => {
    const name = extractCasinoGame(pathname);
    setIframeSrc(`https://stream-s-43.uhdmovies.online/casino-stream?id=${name}`);

    const fetchGameData = async () => {
      try {
        const response = await getCasinoGameDetails(name);
        setGameData(response);
        setSubData(response?.data?.data?.sub || []);
      } catch (err) {
        console.error("Error fetching game data: ", err);
      }
    };

    fetchGameData();
    const interval = setInterval(fetchGameData, 1500);
    return () => clearInterval(interval);
  }, [pathname]);

  useEffect(() => {
    if (gameData) {
      const subData = gameData?.data?.data?.sub || [];
      setSubData(subData);
    }
  }, [gameData]);

  const fetchExposure = useCallback(async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = user?.user_id || user?.id || user?.staff_id || user?.owner_id;
      const matchId = gameData?.data?.data?.mid?.toString() ||
        gameData?.data?.mid?.toString() ||
        gameData?.mid?.toString();

      if (userId && matchId) {
        const response = await getMatchExposure(userId, matchId);
        if (response.success && response.data) {
          const exposureMap = {};
          response.data.forEach((item) => {
            const teamName = item.team_name || item.selection || item.player_name || "";
            const exposureAmount = parseFloat(item.exposure_amount) || 0;
            if (teamName && exposureAmount !== 0) {
              // Create keys for the team name as-is
              const keys = createExposureKeys(teamName);
              keys.forEach(key => {
                if (key) exposureMap[key] = exposureAmount;
              });
            }
          });
          setExposures(exposureMap); // replace, not merge: a settled / finished round's book must drop
        }
      }
    } catch (error) {
      console.error("Error fetching exposure:", error);
    }
  }, [gameData?.data?.data?.mid, gameData?.data?.mid, gameData?.mid]);

  useEffect(() => {
    if (!gameData) return;
    const matchId = gameData?.data?.data?.mid?.toString() ||
      gameData?.data?.mid?.toString() ||
      gameData?.mid?.toString();

    if (matchId) {
      fetchExposure();
      const interval = setInterval(fetchExposure, 2000);
      return () => clearInterval(interval);
    }
  }, [gameData, fetchExposure]);

  const fetchMyBetsData = useCallback(async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = user?.user_id || user?.id || user?.staff_id || user?.owner_id;
      const matchId = gameData?.data?.data?.mid?.toString() ||
        gameData?.data?.mid?.toString() ||
        gameData?.mid?.toString();

      if (userId && matchId) {
        const response = await getMyBets(userId, matchId);
        if (response.success && response.bets) {
          const formattedBets = response.bets.map((bet) => ({
            // My Bets "Gametype" column (casino_bets.game_name, e.g. mogambo)
            description: bet.description ?? bet.game_name ?? bet.gameName,
            userName: bet.userName ?? bet.username,
            placeDate: bet.placeDate ?? bet.created_at,
            matchedBet: bet.player_name || bet.selection || "",
            // the board's "to 1" price (stored odds are decimal)
            odds: displayNetOdds(bet.description, bet.odds || "0"),
            stake: bet.stake || "0",
            type: bet.type || null,
            selection: bet.selection || bet.player_name || "",
            exposer: parseFloat(bet.exposer || bet.exposure_amount || "0") || 0
          }));
          setMyBets(formattedBets);

          // Map exposure from myBets to exposures object with all key variations
          const exposureMap = {};
          response.bets.forEach((bet) => {
            const selection = bet.selection || bet.player_name || "";
            const exposureAmount = parseFloat(bet.exposer || bet.exposure_amount || "0") || 0;
            if (selection) {
              // Create keys for the base selection
              const keys = createExposureKeys(selection);
              keys.forEach(key => {
                if (key) exposureMap[key] = exposureAmount;
              });

              // Also create keys with bet type if selection doesn't already include it
              const betType = bet.type || "";
              if (betType && !selection.toLowerCase().includes(betType.toLowerCase())) {
                const selectionWithType = `${selection} ${betType}`;
                const typeKeys = createExposureKeys(selectionWithType);
                typeKeys.forEach(key => {
                  if (key) exposureMap[key] = exposureAmount;
                });
              }
            }
          });
          setExposures(prev => ({ ...prev, ...exposureMap }));
        }
      }
    } catch (error) {
      console.error("Error fetching my bets:", error);
    }
  }, [gameData?.data?.data?.mid, gameData?.data?.mid, gameData?.mid]);

  useEffect(() => {
    if (!gameData) return;
    const matchId = gameData?.data?.data?.mid?.toString() ||
      gameData?.data?.mid?.toString() ||
      gameData?.mid?.toString();

    if (matchId) {
      fetchMyBetsData();
      const interval = setInterval(fetchMyBetsData, 2000);
      return () => clearInterval(interval);
    }
  }, [gameData, fetchMyBetsData]);

  const handleBetClick = (betData) => {
    if (!betData || !betData.b) return;
    setBetValue(betData.b);
    setBetType("back"); // Default to back for sicbo
    setSelectedSelection(betData?.nat || "");
    setSelectedBetData(betData);
    setShowPlaceBet(true);
  };

  const handleBetPlaced = () => {
    setShowPlaceBet(false);
    setBetValue("");
    setBetType("");
    setSelectedSelection("");
    setSelectedBetData(null);
    // Fetch exposure and my bets after bet placement
    fetchExposure();
    fetchMyBetsData();
  };

  const placedBetCount = myBets.length;
  const roundId = gameData?.data?.data?.mid || gameData?.data?.mid || gameData?.mid || "157250124095658";
  const gameId = roundId?.toString() || "157250124095658";
  const gameNameForApi = extractCasinoGame(pathname) || gameType;
  const playerNameForApi = selectedSelection;

  const feed = gameData?.data?.data ?? {};

  return (
    <CasinoTableLayout
      tableClass="sicbo"
      rightSide={<MyBet bets={myBets} onViewMore={() => setShowViewMoreModal(true)} />}
    >
      <>
        <CasinoHeading
          name={gameName}
          roundId={feed?.mid || ""}
          placedBetCount={placedBetCount}
          activeTab="game"
          onTabChange={() => {}}
        />

        {" "}
        <RefVideo iframeSrc={iframeSrc} timerValue={feed?.lt} rulesType={gameType} />{" "}

        {/* Reference renders .casino-detail once the feed has a round (casinoData.mid) */}
        {feed?.mid ? (
          <div className="casino-detail">
            <BetTableSicbo data={subData} onBetClick={handleBetClick} exposures={exposures} myBets={myBets} />{" "}
            <CasinoLastResult
              gameType={gameType}
              viewAllLink={`/admin/reports/casinoresult/${gameType}`}
              resultComponent={<ResultSicbo key={gameType} gameType={gameType} />}
            />
          </div>
        ) : null}
      </>
      {showPlaceBet && betValue && selectedBetData && (
        <PlaceBet
          betValue={betValue}
          playerName={selectedSelection}
          playerNameForApi={playerNameForApi}
          setShowPlaceBet={setShowPlaceBet}
          gameId={gameId}
          gameName={gameNameForApi}
          roundId={roundId}
          selection={selectedSelection}
          betData={selectedBetData}
          betType={betType}
          onBetPlaced={handleBetPlaced}
        />
      )}
      <ViewMoreModal
        show={showViewMoreModal}
        onHide={() => setShowViewMoreModal(false)}
        title="View More"
        sectionKey={gameType}
      />
    </CasinoTableLayout>
  );
}
